<template>
  <div class="service-info" :class="{ 'loading' : showLoader }">
    <div class="service-details">
      <div class="ui header">{{ $t('components.service.details.header') }}</div>
      <div class="ui divider"></div>

      <semantic-loading :active="showLoader"/>

      <template v-if="showMobilePayNotice">
        <div class="ui horizontal red header" style="text-align: center;">
          {{ $t('pages.dashboard.details.mobile_pay_instant_notice') }}
        </div>
        <div class="ui divider"></div>
      </template>

      <div class="ui zw actions" v-if="service">
        <button class="ui blue button"
                v-if="canPayOverdueFee"
                :disabled="isButtonDisabled || service.overdue_fee_payed ? 'disabled' : false"
                @click="showPayOverdueFeeModal = !showPayOverdueFeeModal">
              <span v-if="service.overdue_fee_payed">
                {{ $t('globals.buttons.overdue_fee_paid') }}
              </span>
          <span v-else>
                {{ $t('globals.buttons.pay_overdue_fee') }}
              </span>
        </button>
        <button class="ui blue button"
                v-if="canPayOverdue"
                :disabled="isButtonDisabled || (service.apply_overdue_fee && service.product.overdue_fee_amount !== null && !service.overdue_fee_payed) ? 'disabled' : false"
                @click="showPayOverdueModal = !showPayOverdueModal">
          {{ $t('globals.buttons.pay_overdue_payment') }}
        </button>
      </div>

      <form class="ui zw form" v-if="service" @submit.prevent="saveEditForm('serviceDetails')"
            data-vv-scope="serviceDetails">
        <div class="two fields" v-for="(fields, index) in chunkProperty(service.product.fields, 2)" :key="index">
          <field :field="field" :allowEdit="editIsAllowed" :isactive="service.status === 'active'"
                 :scope="'serviceDetails'" @input="changeFieldValue" :value="fieldValues" :transparent="!editIsAllowed"
                 v-if="field" v-for="field in fields" :key="field.id"/>
        </div>

        <div class="actions" v-if="editIsAllowed">
          <button type="submit" class="ui blue circular button">{{ $t('globals.buttons.save') }}</button>
          <button class="ui circular button" @click.prevent="cancel">{{ $t('globals.buttons.cancel') }}</button>
        </div>
      </form>
    </div>

    <div class="transactions" v-if="processedTransactions && processedTransactions.length > 0">
      <div class="ui header">{{ $t('components.service.transactions.header') }}</div>
      <div class="ui divider"></div>

      <div class="ui segments">
        <transaction v-for="transaction in processedTransactions" :transaction="transaction" :waitlist="true"
                     :key="transaction.id" :amount="transaction.amount"/>
      </div>
    </div>

    <modal ref="update_card_modal" :active="showUpdatePaymentCardModal && isOverdueChargedFailed"
           :title="$t('modals.update_payment_card.title')" icons="big alarm" @acceptmodal="updatePaymentInformation"
           @denymodal="closeUpdatePaymentCardModal">
      <h4>{{ $t('modals.update_payment_card.header') }}</h4>
      <span v-html="$t('modals.update_payment_card.body')"></span>
    </modal>

    <semantic-modal :active="showPayOverdueModal" v-if="service.status === 'overdue'" class="small"
                    :settings="{content_classes: {content: false}, closeable: true, closeable_button: true}">
      <div class="header" slot="header">
        <h1>{{ $t('modals.overdue_payment_notice', {amount: service.pricing_amount}) }}</h1>
      </div>

      <div class="actions" slot="actions">
        <div class="ui deny button">{{ $t('globals.buttons.no') }}</div>
        <div class="ui green ok button" @click="payOverdue">{{ $t('globals.buttons.yes') }}</div>
      </div>
    </semantic-modal>

    <semantic-modal :active="showPayOverdueFeeModal" v-if="service.status === 'overdue'" class="small"
                    :settings="{content_classes: {content: false}, closeable: true, closeable_button: true}">
      <div class="header" slot="header">
        <h1>{{ $t('modals.nets_notice') }}</h1>
      </div>

      <div class="actions" slot="actions">
        <div class="ui deny button">{{ $t('globals.buttons.no') }}</div>
        <div class="ui green ok button" @click="payOverdueFee">{{ $t('globals.buttons.yes') }}</div>
      </div>
    </semantic-modal>

    <loading :active.sync="isLoading"
             :is-full-page="fullPage"/>
  </div>
</template>

<script>
import chunk from 'lodash/chunk'
import findIndex from 'lodash/findIndex'
import find from 'lodash/find'
import moment from "moment/moment";
import Loading from "vue-loading-overlay";

export default {
  name: 'ServiceDetails',
  components: {Loading},

  props: {
    service: {
      required: true,
      default: null,
      type: Object
    },

    waitlist: {
      required: false,
      default: false,
      type: Boolean
    }
  },

  data() {
    return {
      fieldValues: [],
      showLoader: false,
      isLoading: false,
      fullPage: true,
      showPayOverdueModal: false,
      showPayOverdueFeeModal: false,
      isOverdueChargedFailed: false,
      showUpdatePaymentCardModal: false,
      processedTransactions: {}
    }
  },

  watch: {
    service: function () {
      this.fieldValues = []

      this.fetchProcessedTransactions()
    }
  },

  mounted() {
    this.fetchProcessedTransactions()
  },

  methods: {
    chunkProperty(chunkedProperty, value) {
      return chunk(chunkedProperty, value)
    },

    changeFieldValue(field) {
      if (!find(this.fieldValues, {id: field.id})) {
        this.fieldValues.push(field)
      } else {
        var arrayValue = find(this.fieldValues, {id: field.id})
        if (field.value !== '') {
          arrayValue.value = field.value
        } else {
          this.fieldValues.splice(findIndex(this.fieldValues, {id: field.id}), 1)
        }
      }
    },

    saveEditForm(scope) {
      this.$validator.validateAll(scope)
        .then(result => {
          if (result) {
            // Continue with form
            this.showLoader = true // Showing the loader
            const fields = []
            // handle editableFields
            $.map(this.fieldValues, function (item, index) {
              if (item.editable) {
                if (item.required) {
                  if (item.value !== '') {
                    fields.push({id: item.id, value: item.value})
                  }
                } else {
                  if (item.value === '') {
                    fields.push({id: item.id, value: null})
                  } else {
                    fields.push({id: item.id, value: item.value})
                  }
                }
              }
            })

            // make update request
            if (fields) {
              const url = (this.waitlist) ? `waitlistsubscriptions/${this.service.id}` : `subscriptions/${this.service.id}`
              this.axios.put(url, {
                fields: fields
              })
                .then(res => {
                  this.$parent.fetchService(this.service.id)
                  this.showLoader = false

                  this.$events.$emit('alert', {
                    header: this.$t('globals.validation.success.success'),
                    message: this.$t('components.service.validation.success.details_updated'),
                    type: 'success',
                    align: 'right'
                  })
                }, () => {
                  this.$parent.fetchService(this.service.id)
                  this.showLoader = false

                  this.$events.$emit('alert', {
                    header: this.$t('globals.validation.errors.something_went_wrong'),
                    message: this.$t('globals.validation.errors.try_again_later'),
                    type: 'error',
                    align: 'right'
                  })
                }).catch()
            } else {
              this.showLoader = false
            }
          }
        })
    },

    cancel() {
      this.$parent.fetchService(this.service.id)
        .then(() => {
          this.showLoader = false
          this.fieldValues = []
        })
    },

    fetchProcessedTransactions() {
      this.axios.get(`waitlistsubscriptions/${this.service.id}/transactions`, {
        params: {
          perPage: 3,
          page: 1
        }
      })
        .then(({data}) => {
          this.processedTransactions = data.data
        }).catch()
    },

    payOverdue() {
      this.showPayOverdueModal = false
      this.isLoading = true
      this.axios.post(`waitlistsubscriptions/${this.service.id}/overdue/pay`).then(({data}) => {
        if (data.payment_method === 'mobile_pay') {
          let redirectUrl = data.url

          if (process.env.NODE_ENV === 'development') {
            this.$router.push({
              name: 'mobile_pay_success',
              query: {
                id: this.service.id,
                type: 'waitlist',
                op: 'status'
              }
            })
          } else {
            window.location.href = redirectUrl
          }
        } else if (data.payment_method === 'vipps') {
          window.location.href = data.url
        } else {
          this.isLoading = false
          let waitlist = data.model

          if (waitlist.status === 'active') {
            this.$events.$emit('alert', {
              header: this.$t('globals.validation.success.success'),
              message: this.$t('globals.validation.success.overdue_payment_successful'),
              type: 'success',
              align: 'right'
            })

            this.$parent.fetchService(this.service.id)
          } else {
            this.$events.$emit('alert', {
              header: this.$t('globals.validation.errors.error'),
              message: this.$t('globals.validation.errors.overdue_payment_failed'),
              type: 'warning',
              align: 'right'
            })

            this.isOverdueChargedFailed = !this.isOverdueChargedFailed
            this.showUpdatePaymentCardModal = !this.showUpdatePaymentCardModal

            this.$parent.fetchService(this.service.id)
          }
        }
      }).catch((error) => {
        this.isLoading = false
        if (error.response.data.message) {
          this.$events.$emit('alert', {
            header: this.$t('globals.validation.errors.error'),
            message: error.response.data.message,
            type: 'error',
            align: 'right'
          })
        }
        this.$events.$emit('updateactivesubscription')
      })
    },

    updatePaymentInformation() {
      this.$refs.update_card_modal.denyModal()
      this.$nextTick(() => {
        setTimeout(() => {
          this.$router.push({
            name: 'checkout',
            params: {containerId: this.service.id},
            query: {container: 'waitlist', subscriptionId: this.service.nets_details.subscription_id}
          })
        }, 500)
      })
    },

    closeUpdatePaymentCardModal() {
      this.isOverdueChargedFailed = false
      this.showUpdatePaymentCardModal = false
    },

    payOverdueFee() {
      if (this.service.payment_method === 'mobile_pay' || this.service.payment_method === 'vipps') {
        this.axios.post(`waitlistsubscriptions/${this.service.id}/overdue/fee/payment`).then(({data}) => {
          if (data.success) {
            window.location.href = data.url
          } else {
            this.$events.$emit('alert', {
              header: this.$t('globals.validation.errors.something_went_wrong'),
              message: this.$t('globals.validation.errors.try_again_later'),
              type: 'error',
              align: 'right'
            })

            this.showPayOverdueFeeModal = !this.showPayOverdueFeeModal
          }
        })
      } else {
        this.$router.push({name: 'checkout_overdue_fee', params: {id: this.service.id, container: 'waitlist'}})
      }
    }
  },

  computed: {
    editIsAllowed() {
      return this.service.status === 'active'
    },

    showMobilePayNotice () {
      return this.service.payment_method === 'vipps' &&
        this.service.status === 'active' &&
        this.service.pricing.type === 'instant' &&
        this.service.transaction_date > moment().format('YYYY-MM-DD')
    },

    canPayOverdueFee() {
      if (this.service.status === 'overdue' &&
        this.service.product.overdue_fee_amount !== null &&
        this.service.apply_overdue_fee) {
        if (this.service.payment_method === 'mobile_pay') {
          return this.$store.getters.activeMerchant.vipps
        } else {
          return true
        }
      }

      return false
    },

    canPayOverdue() {
      if (this.service.status === 'overdue') {
        if (this.service.payment_method === 'mobile_pay') {
          return this.$store.getters.activeMerchant.vipps
        } else {
          return true
        }
      }

      return false
    },

    isButtonDisabled() {
      return this.$store.getters.activeMerchant && this.$store.getters.activeMerchant.opus && !this.$auth.user().social_number
    }

    /* activeAccessories () {
      const accessories = this.service.accessories ? JSON.parse(this.service.accessories) : null
      const retAccesories = []
      const _this = this

      if (accessories) {
        $.map(accessories, function (amount, accessoryId) {
          retAccesories.push(find(_this.service.product.accessories, { id: parseInt(accessoryId) }))
        })
      }

      return retAccesories
    } */
  }
}
</script>

<style lang="scss" scoped>
.service-info {
  position: relative;
  background: #fff;
  padding: 1.2rem;
  border-radius: 15px;
  margin: .5rem 0;
  height: auto;

  &.loading:before {
    content: "";
    position: absolute;
    border-radius: 15px;
    top: 0;
    right: 0;
    left: 0;
    bottom: 0;
    background: rgba(255, 255, 255, .5);
    z-index: 100000;
  }

  > .service-details {
    position: relative;
    height: auto;

    .actions {
      display: flex;
      align-items: center;
      justify-content: flex-end;
      flex-flow: row;
      padding: 1rem;
    }
  }
}
</style>



// WEBPACK FOOTER //
// src/components/subscription/ServiceDetails.vue